import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import type { FilterState } from '../types';
import { industryLabel, stateLabel, statusLabel, useT } from '../i18n';

interface Props {
  filters: FilterState;
  /** True when the date range is narrower than the full available span. */
  dateActive: boolean;
  dateLabel: string;
  onResetDate: () => void;
  onRemoveValue: (key: keyof FilterState, value: string) => void;
  // A chart-click drill (industry / country / Wahlkreis). It scopes the table
  // + map but isn't part of `filters`, so it's surfaced here explicitly.
  selection?: { kind: string; label: string } | null;
  onClearSelection?: () => void;
  /** Wipes everything — panel filters, date range, chart-click selections.
      Surfaced as a red "Alles zurücksetzen" pill at the start of the bar
      whenever any filter is active. */
  onClearAll: () => void;
}

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: var(--sans-stack);
`;

const Caption = styled.span`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-right: ${theme.spacing.xs};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px 3px 10px;
  border-radius: 999px;
  background: var(--color-data-100);
  border: 1px solid color-mix(in srgb, var(--color-data) 40%, transparent);
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
`;

const ChipKey = styled.span`
  color: ${theme.colors.text.muted};
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  &:hover { background: color-mix(in srgb, var(--color-data) 35%, transparent); color: ${theme.colors.text.primary}; }
`;

// "Reset everything" pill at the start of the bar. Mirrors the chip shape
// but swaps the data-tint for the error red — the chip is destructive
// (wipes panel filters + date + chart-click selections) so it should not
// blend in with the per-chip × buttons.
const ResetAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, ${theme.colors.error} 10%, transparent);
  border: 1px solid color-mix(in srgb, ${theme.colors.error} 45%, transparent);
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.xs};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.error};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: color-mix(in srgb, ${theme.colors.error} 22%, transparent);
    border-color: ${theme.colors.error};
  }
`;

// Chip-able dimensions. Investment type is deliberately excluded — it is
// already controlled (and made visible) by the tier toggle and the chart
// legends, so surfacing it again here would create two competing controls.
// Labels live in the dictionary (chips.keys).
const CHIP_KEYS = ['industries', 'federalStates', 'investmentStatuses', 'companies'] as const;

// Industry filter values arrive as "L<level>:<label>"; show only the label.
// The value itself stays the (German) key; only its display is translated.
function displayValue(key: keyof FilterState, value: string): string {
  if (key === 'industries') return industryLabel(value.replace(/^L\d:/, ''));
  if (key === 'federalStates') return stateLabel(value);
  if (key === 'investmentStatuses') return statusLabel(value);
  return value;
}

export function ActiveFilterChips({ filters, dateActive, dateLabel, onResetDate, onRemoveValue, selection, onClearSelection, onClearAll }: Props) {
  const m = useT();
  const hasAny = dateActive || !!selection || filters.search !== '' || CHIP_KEYS.some(k => (filters[k] as string[]).length > 0);
  if (!hasAny) return null;

  return (
    <Bar>
      <ResetAll onClick={onClearAll} type="button" aria-label={m.chips.resetAllAria}>
        {m.chips.resetAll}
      </ResetAll>
      <Caption>{m.chips.caption}</Caption>
      {selection && (
        <Chip>
          <ChipKey>{selection.kind}</ChipKey>
          {selection.label}
          <RemoveBtn onClick={onClearSelection} aria-label={m.chips.clearSelection} title={m.chips.clearSelection}>×</RemoveBtn>
        </Chip>
      )}
      {dateActive && (
        <Chip>
          <ChipKey>{m.chips.period}</ChipKey>
          {dateLabel}
          <RemoveBtn onClick={onResetDate} aria-label={m.chips.resetPeriod} title={m.chips.resetPeriod}>×</RemoveBtn>
        </Chip>
      )}
      {/* `search` is the one non-array filter — a single chip for the term. */}
      {filters.search !== '' && (
        <Chip>
          <ChipKey>{m.chips.search}</ChipKey>
          {m.chips.quoted(filters.search)}
          <RemoveBtn onClick={() => onRemoveValue('search', filters.search)} aria-label={m.chips.removeSearch} title={m.chips.removeSearch}>×</RemoveBtn>
        </Chip>
      )}
      {CHIP_KEYS.flatMap(key => {
        const values = filters[key] as string[];
        return values.map(value => (
          <Chip key={`${key}:${value}`}>
            <ChipKey>{m.chips.keys[key]}</ChipKey>
            {displayValue(key, value)}
            <RemoveBtn onClick={() => onRemoveValue(key, value)} aria-label={m.common.removeFilter} title={m.common.removeFilter}>×</RemoveBtn>
          </Chip>
        ));
      })}
    </Bar>
  );
}
